import type { LegislationTypeCode } from '@artem-lex/common-server/radar/legislationTypes'
import { apiClient } from '@/shared/api/apiClient'
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query'

export type ScopeTierStatus = 'pending' | 'approved' | 'flagged' | 'trashed'
export type ScopeTierStatusFilter = ScopeTierStatus | 'edited' | 'extension_review_needed' | 'consolidation_review_needed' | 'all'
export const SCOPE_TIER_IN_FORCE_FILTER_VALUES = ['in_force', 'not_in_force', 'all'] as const
export type ScopeTierInForceFilter = (typeof SCOPE_TIER_IN_FORCE_FILTER_VALUES)[number]
export type RenderMode = 'auto' | 'rendered' | 'raw'

export type ScopeTierConsolidationProvenance = {
  consolidatedCelex: string | null
  consolidationVersionDate: string | null
  scopeCarriedForward: boolean | null
  embeddingReleaseStatus: 'pending' | 'complete' | 'failed' | null
}

export type ScopeTierDocumentListItem = {
  documentId: string
  celex: string
  inForce: boolean
  title: string
  type: string | null
  tags: string[]
  currentTier: 1 | 2 | 3 | null
  status: ScopeTierStatus
  hasExpertSpan: boolean
  updatedAt: string
  reviewedBy: string | null
  version: string
} & ScopeTierConsolidationProvenance

export type ScopeTierDocumentDetailResponse = {
  document: {
    documentId: string
    celex: string
    inForce: boolean
    title: string
    originalTitle: string | null
    type: string | null
    documentUrl: string | null
    tags: string[]
    modeDefault: RenderMode
  } & ScopeTierConsolidationProvenance
  review: {
    status: ScopeTierStatus
    reviewedAt: string | null
    reviewedBy: string | null
  }
  tierState: {
    currentTier: 1 | 2 | 3 | null
    sourceTier: 1 | 2 | 3 | null
    activeSpanSource: 'expert' | 'spans'
  }
  spans: Array<{
    spanId: string
    scopeTier: number
    startBlockId: string
    endBlockId: string
  }>
  blocks: Array<{
    blockId: string
    blockOrder: number
    rawText: string
  }>
  expertSpan: {
    spanId: string
    startBlockId: string
    endBlockId: string
    startCharOffset?: number | null
    endCharOffset?: number | null
    selectedText: string
    normalizedCelex?: string | null
  } | null
  /** Latest extension capture review-needed record (includes Plan A canonical blocks from upload when present). */
  extensionReviewCapture: {
    selectedText: string
    sourceUrl: string
    normalizedCelex: string
    reason: 'no_match' | 'ambiguous_match'
    captureMode: 'network_fetch' | 'dom_snapshot' | null
    clientHints: { browser?: string; extensionVersion?: string } | null
    canonicalBlocks: Array<{
      blockId: string
      blockOrder: number
      rawText: string
    }> | null
    capturedAt: string
  } | null
  lastUpdate: {
    updatedAt: string
    reviewedBy: string | null
    version: string
  }
}

export type LegislationDocumentResponse = {
  documentId: string
  celex: string
  title: string
  blocks: Array<{
    blockId: string
    blockOrder: number
    rawText: string
  }>
}

export type LegislationDocumentByCelexResponse = {
  documentId: string
  celex: string
  title: string
  startBlockId: string
  endBlockId: string
}

type ScopeTierLastUpdate = Pick<ScopeTierDocumentListItem, 'updatedAt' | 'reviewedBy' | 'version'>

type SetScopeTierStatusResponse = {
  ok: boolean
  review: {
    status: ScopeTierStatus
    reviewedAt: string | null
    reviewedBy: string | null
  }
  lastUpdate: ScopeTierLastUpdate
}

type SetScopeTierValueResponse = {
  ok: boolean
  tierState: {
    currentTier: 1 | 2 | 3
    sourceTier: 1 | 2 | 3 | null
  }
  lastUpdate: ScopeTierLastUpdate
}

type SetScopeTierExpertSpanResponse = {
  ok: boolean
  expertSpan: unknown
  lastUpdate: ScopeTierLastUpdate
}

type ResolveScopeTierExtensionReviewResponse = {
  ok: boolean
  resolved: boolean
  lastUpdate: ScopeTierLastUpdate
}

type RetryScopeTierMaterializationResponse = {
  ok: boolean
  status: 'queued' | 'skipped'
  reason?: string
  embeddingReleaseStatus: 'pending' | 'complete' | 'failed' | null
}

type ScopeTierListResponse = {
  items: ScopeTierDocumentListItem[]
  pageInfo: { nextCursor: string | null; totalApprox?: number }
  tierCounts: Record<1 | 2 | 3, number>
  unassignedCount: number
  globalPendingCount: number
  extensionReviewNeededCount: number
  consolidationReviewNeededCount: number
  selection: {
    selectedDocumentId: string | null
    prevDocumentId: string | null
    nextDocumentId: string | null
  }
}

export type ScopeTierListFilters = {
  // oxlint-disable-next-line id-length -- API contract uses "q" for query
  q?: string
  tiers?: Array<1 | 2 | 3>
  tags?: string[]
  types?: LegislationTypeCode[]
  includeUnassigned?: boolean
  inForceFilter?: ScopeTierInForceFilter
  status?: ScopeTierStatusFilter
  includeTrashed?: boolean
  selectedDocumentId?: string
  cursor?: string
  limit?: number
}

export type ScopeTierBatchSearchFilters = {
  // oxlint-disable-next-line id-length -- API contract uses "q" for query
  q: string
  sectionQuery?: string
  excludedDocumentIds?: string[]
  tiers?: Array<1 | 2 | 3>
  types?: LegislationTypeCode[]
  includeUnassigned?: boolean
  inForceFilter?: ScopeTierInForceFilter
  status?: ScopeTierStatusFilter
  includeTrashed?: boolean
  updatedBefore?: string
  cursor?: string
  limit?: number
}

export type ScopeTierBatchSearchListItem = ScopeTierDocumentListItem & {
  previewBlockId: string
  previewBlockOrder: number
  previewRawText: string
  previewMatchStart: number
  totalMatchesInDoc: number
}

export type ScopeTierBatchSearchResponse = {
  items: ScopeTierBatchSearchListItem[]
  pageInfo: { nextCursor: string | null }
  totalMatchedDocuments: number
  totalMatchedParagraphs: number
  sectionMatchMode: 'best_effort' | 'none'
  sectionWindowSize: number
  ordering: {
    primary: 'documentId'
    direction: 'asc'
  }
}

type ScopeTierBatchDocumentMatchesResponse = {
  documentId: string
  items: Array<{
    blockId: string
    blockOrder: number
    rawText: string
    matchStart: number
  }>
  sectionMatchMode: 'best_effort' | 'none'
  sectionWindowSize: number
}

type SetScopeTierStatusesBulkResponse = {
  ok: boolean
  requestedCount: number
  updatedCount: number
  notFoundCount: number
  failedCount: number
  failedDocumentIds: string[]
}

type ScopeTierTagsResponse = {
  items: Array<{
    name: string
    normalized: string
  }>
}

export type ScopeTierBatchActionInput =
  | { status: ScopeTierStatus; documentIds: string[] }
  | { action: 'set_status'; status: ScopeTierStatus; documentIds: string[] }
  | { action: 'add_tags'; tags: string[]; documentIds: string[] }
  | { action: 'remove_tags'; tags: string[]; documentIds: string[] }

const RADAR_SCOPE_TIER_KEY = 'radar-scope-tier'
const DOCUMENTS_QUERY_KEY = [RADAR_SCOPE_TIER_KEY, 'documents'] as const
const RECONCILE_LIST_DEBOUNCE_MS = 2000

let listReconcileTimeout: ReturnType<typeof setTimeout> | null = null

type ScopeTierDocumentsInfiniteData = InfiniteData<ScopeTierListResponse, string | null>

type ScopeTierDocumentPatch = Partial<
  Pick<
    ScopeTierDocumentListItem,
    'status' | 'currentTier' | 'hasExpertSpan' | 'updatedAt' | 'reviewedBy' | 'version'
  >
>

function patchScopeTierDocumentsCache(
  queryClient: QueryClient,
  documentId: string,
  patch: ScopeTierDocumentPatch
) {
  queryClient.setQueriesData<ScopeTierDocumentsInfiniteData>(
    { queryKey: DOCUMENTS_QUERY_KEY },
    (current) => {
      if (!current) {
        return current
      }

      let changed = false
      const pages = current.pages.map((page) => {
        let pageChanged = false
        const items = page.items.map((item) => {
          if (item.documentId !== documentId) {
            return item
          }
          pageChanged = true
          changed = true
          return { ...item, ...patch }
        })
        return pageChanged ? { ...page, items } : page
      })

      return changed ? { ...current, pages } : current
    }
  )
}

function scheduleScopeTierDocumentsReconcile(queryClient: QueryClient) {
  if (listReconcileTimeout) {
    clearTimeout(listReconcileTimeout)
  }

  listReconcileTimeout = setTimeout(() => {
    void queryClient.invalidateQueries({
      queryKey: DOCUMENTS_QUERY_KEY,
      refetchType: 'active',
    })
    listReconcileTimeout = null
  }, RECONCILE_LIST_DEBOUNCE_MS)
}

export function useScopeTierDocuments(
  filters: ScopeTierListFilters,
  options?: { enabled?: boolean; refetchInterval?: number | false }
) {
  return useInfiniteQuery<ScopeTierListResponse, Error>({
    queryKey: [RADAR_SCOPE_TIER_KEY, 'documents', filters],
    initialPageParam: filters.cursor ?? null,
    queryFn: async ({ pageParam }) => {
      const params = {
        ...filters,
        cursor: pageParam ?? undefined,
        tags: filters.tags && filters.tags.length > 0 ? filters.tags.join(',') : undefined,
        types: filters.types && filters.types.length > 0 ? filters.types.join(',') : undefined,
        tiers:
          filters.tiers === undefined
            ? undefined
            : filters.tiers.length === 0
              ? 'none'
              : filters.tiers.join(','),
      }
      const response = await apiClient.get<ScopeTierListResponse>('/radar/scope-tier/documents', {
        params,
      })
      return response.data
    },
    getNextPageParam: (lastPage) => lastPage.pageInfo.nextCursor,
    enabled: options?.enabled ?? true,
    refetchInterval: options?.refetchInterval,
  })
}

export function useScopeTierDocument(
  documentId: string | null,
  options?: { enabled?: boolean }
) {
  return useQuery<ScopeTierDocumentDetailResponse, Error>({
    queryKey: [RADAR_SCOPE_TIER_KEY, 'document', documentId],
    queryFn: async () => {
      if (!documentId) {
        throw new Error('Document ID is required')
      }
      const response = await apiClient.get<ScopeTierDocumentDetailResponse>(
        `/radar/scope-tier/documents/${documentId}`
      )
      return response.data
    },
    enabled: (options?.enabled ?? true) && !!documentId,
    // Detail panel shows embedding status; poll while release is pending so the
    // label clears when the background job finishes (list already polls).
    refetchInterval(query) {
      if (query.state.data?.document.embeddingReleaseStatus === 'pending') {
        return 15_000
      }
      return false
    },
  })
}

export function useLegislationDocument(
  documentId: string | null,
  options?: { enabled?: boolean; refetchInterval?: number | false }
) {
  return useQuery<LegislationDocumentResponse, Error>({
    queryKey: [RADAR_SCOPE_TIER_KEY, 'legislation-document', documentId],
    queryFn: async () => {
      if (!documentId) {
        throw new Error('Document ID is required')
      }
      const response = await apiClient.get<LegislationDocumentResponse>(
        `/radar/documents/${documentId}`
      )
      return response.data
    },
    enabled: (options?.enabled ?? true) && !!documentId,
    refetchInterval: options?.refetchInterval,
  })
}

export async function fetchLegislationDocumentByCelex(
  celex: string
): Promise<LegislationDocumentByCelexResponse | null> {
  const normalizedCelex = celex.trim().toUpperCase()
  if (normalizedCelex.length === 0) {
    return null
  }
  try {
    const response = await apiClient.get<LegislationDocumentByCelexResponse>(
      `/radar/documents/by-celex/${encodeURIComponent(normalizedCelex)}`
    )
    return response.data
  } catch (error) {
    if (
      typeof error === 'object' &&
      error != null &&
      'response' in error &&
      typeof (error as { response?: { status?: number } }).response?.status ===
        'number' &&
      (error as { response: { status: number } }).response.status === 404
    ) {
      return null
    }
    throw error
  }
}

export function useScopeTierBatchSearch(
  filters: ScopeTierBatchSearchFilters,
  options?: { enabled?: boolean; queryKeySuffix?: number | string }
) {
  return useInfiniteQuery<ScopeTierBatchSearchResponse, Error>({
    queryKey: [RADAR_SCOPE_TIER_KEY, 'batch-search', options?.queryKeySuffix ?? null, filters],
    initialPageParam: filters.cursor ?? null,
    queryFn: async ({ pageParam }) => {
      const params = {
        ...filters,
        excludedDocumentIds:
          filters.excludedDocumentIds && filters.excludedDocumentIds.length > 0
            ? filters.excludedDocumentIds.join(',')
            : undefined,
        cursor: pageParam ?? undefined,
        types: filters.types && filters.types.length > 0 ? filters.types.join(',') : undefined,
        tiers:
          filters.tiers === undefined
            ? undefined
            : filters.tiers.length === 0
              ? 'none'
              : filters.tiers.join(','),
      }
      const response = await apiClient.get<ScopeTierBatchSearchResponse>('/radar/scope-tier/batch-search', {
        params,
      })
      return response.data
    },
    getNextPageParam: (lastPage) => lastPage.pageInfo.nextCursor,
    enabled: options?.enabled ?? true,
  })
}

export function useScopeTierBatchDocumentMatches(
  documentId: string | null,
  // oxlint-disable-next-line id-length -- API param name is "q"
  filters: { q: string; sectionQuery?: string; limit?: number },
  options?: { enabled?: boolean }
) {
  return useQuery<ScopeTierBatchDocumentMatchesResponse, Error>({
    queryKey: [RADAR_SCOPE_TIER_KEY, 'batch-document-matches', documentId, filters],
    queryFn: async () => {
      if (!documentId) {
        throw new Error('Document ID is required')
      }
      const response = await apiClient.get<ScopeTierBatchDocumentMatchesResponse>(
        `/radar/scope-tier/batch-search/${documentId}/matches`,
        {
          params: filters,
        }
      )
      return response.data
    },
    enabled: (options?.enabled ?? true) && !!documentId && filters.q.trim().length >= 4,
  })
}

export function useScopeTierTags(filters?: { query?: string; limit?: number }) {
  return useQuery<ScopeTierTagsResponse, Error>({
    queryKey: [RADAR_SCOPE_TIER_KEY, 'tags', filters?.query ?? '', filters?.limit ?? 50],
    queryFn: async () => {
      const response = await apiClient.get<ScopeTierTagsResponse>('/radar/scope-tier/tags', {
        params: {
          q: filters?.query || undefined,
          limit: filters?.limit ?? 50,
        },
      })
      return response.data
    },
  })
}

export function useSetScopeTierStatus(documentId: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (status: ScopeTierStatus) => {
      if (!documentId) {
        throw new Error('Document ID is required')
      }
      const response = await apiClient.patch<SetScopeTierStatusResponse>(
        `/radar/scope-tier/documents/${documentId}/status`,
        { status }
      )
      return response.data
    },
    onSuccess: (data, status) => {
      if (documentId) {
        patchScopeTierDocumentsCache(queryClient, documentId, {
          status,
          updatedAt: data.lastUpdate.updatedAt,
          reviewedBy: data.lastUpdate.reviewedBy,
          version: data.lastUpdate.version,
        })
      }
      if (documentId) {
        void queryClient.invalidateQueries({ queryKey: [RADAR_SCOPE_TIER_KEY, 'document', documentId] })
      }
      scheduleScopeTierDocumentsReconcile(queryClient)
    },
  })
}

export function useSetScopeTierStatusesBulk() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: ScopeTierBatchActionInput) => {
      const response = await apiClient.post<SetScopeTierStatusesBulkResponse>(
        '/radar/scope-tier/batch-actions',
        input
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEY })
      void queryClient.invalidateQueries({ queryKey: [RADAR_SCOPE_TIER_KEY, 'batch-search'] })
      void queryClient.invalidateQueries({ queryKey: [RADAR_SCOPE_TIER_KEY, 'tags'] })
    },
  })
}

export function useSetScopeTierValue(documentId: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (tier: 1 | 2 | 3) => {
      if (!documentId) {
        throw new Error('Document ID is required')
      }
      const response = await apiClient.patch<SetScopeTierValueResponse>(
        `/radar/scope-tier/documents/${documentId}/tier`,
        {
        tier,
        }
      )
      return response.data
    },
    onSuccess: (data, tier) => {
      if (documentId) {
        patchScopeTierDocumentsCache(queryClient, documentId, {
          currentTier: tier,
          updatedAt: data.lastUpdate.updatedAt,
          reviewedBy: data.lastUpdate.reviewedBy,
          version: data.lastUpdate.version,
        })
      }
      if (documentId) {
        void queryClient.invalidateQueries({ queryKey: [RADAR_SCOPE_TIER_KEY, 'document', documentId] })
      }
      scheduleScopeTierDocumentsReconcile(queryClient)
    },
  })
}

export function useSaveExpertScopeSpan(documentId: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      startBlockId: string
      endBlockId: string
      startCharOffset?: number
      endCharOffset?: number
      selectedText: string
      normalizedCelex?: string
    }) => {
      if (!documentId) {
        throw new Error('Document ID is required')
      }
      const response = await apiClient.patch<SetScopeTierExpertSpanResponse>(
        `/radar/scope-tier/documents/${documentId}/expert-span`,
        {
          action: 'save',
          ...input,
        }
      )
      return response.data
    },
    onSuccess: (data) => {
      if (documentId) {
        patchScopeTierDocumentsCache(queryClient, documentId, {
          hasExpertSpan: true,
          updatedAt: data.lastUpdate.updatedAt,
          reviewedBy: data.lastUpdate.reviewedBy,
          version: data.lastUpdate.version,
        })
      }
      if (documentId) {
        void queryClient.invalidateQueries({ queryKey: [RADAR_SCOPE_TIER_KEY, 'document', documentId] })
      }
      scheduleScopeTierDocumentsReconcile(queryClient)
    },
  })
}

export function useClearExpertScopeSpan(documentId: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (spanId?: string) => {
      if (!documentId) {
        throw new Error('Document ID is required')
      }
      const response = await apiClient.patch<SetScopeTierExpertSpanResponse>(
        `/radar/scope-tier/documents/${documentId}/expert-span`,
        {
          action: 'clear',
          spanId,
        }
      )
      return response.data
    },
    onSuccess: (data) => {
      if (documentId) {
        patchScopeTierDocumentsCache(queryClient, documentId, {
          hasExpertSpan: false,
          updatedAt: data.lastUpdate.updatedAt,
          reviewedBy: data.lastUpdate.reviewedBy,
          version: data.lastUpdate.version,
        })
      }
      if (documentId) {
        void queryClient.invalidateQueries({ queryKey: [RADAR_SCOPE_TIER_KEY, 'document', documentId] })
      }
      scheduleScopeTierDocumentsReconcile(queryClient)
    },
  })
}

export function useRetryScopeTierMaterialization(documentId: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      if (!documentId) {
        throw new Error('Document ID is required')
      }
      const response = await apiClient.post<RetryScopeTierMaterializationResponse>(
        `/radar/scope-tier/documents/${documentId}/retry-materialization`,
        {}
      )
      return response.data
    },
    onSuccess: () => {
      if (documentId) {
        void queryClient.invalidateQueries({
          queryKey: [RADAR_SCOPE_TIER_KEY, 'document', documentId],
        })
      }
      void queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEY })
    },
  })
}

export function useResolveExtensionReview(documentId: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      if (!documentId) {
        throw new Error('Document ID is required')
      }
      const response = await apiClient.post<ResolveScopeTierExtensionReviewResponse>(
        `/radar/scope-tier/documents/${documentId}/extension-review/resolve`,
        {}
      )
      return response.data
    },
    onSuccess: (data) => {
      if (documentId) {
        patchScopeTierDocumentsCache(queryClient, documentId, {
          updatedAt: data.lastUpdate.updatedAt,
          reviewedBy: data.lastUpdate.reviewedBy,
          version: data.lastUpdate.version,
        })
      }
      if (documentId) {
        void queryClient.invalidateQueries({ queryKey: [RADAR_SCOPE_TIER_KEY, 'document', documentId] })
      }
      scheduleScopeTierDocumentsReconcile(queryClient)
    },
  })
}

export function useAddScopeTierTags(documentId: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { tags: string[] }) => {
      if (!documentId) {
        throw new Error('Document ID is required')
      }
      const response = await apiClient.post(`/radar/scope-tier/documents/${documentId}/tags`, input)
      return response.data
    },
    onSuccess: () => {
      if (documentId) {
        void queryClient.invalidateQueries({ queryKey: [RADAR_SCOPE_TIER_KEY, 'document', documentId] })
      }
      void queryClient.invalidateQueries({ queryKey: [RADAR_SCOPE_TIER_KEY, 'documents'] })
      void queryClient.invalidateQueries({ queryKey: [RADAR_SCOPE_TIER_KEY, 'tags'] })
    },
  })
}

export function useRemoveScopeTierTags(documentId: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { tags: string[] }) => {
      if (!documentId) {
        throw new Error('Document ID is required')
      }
      const response = await apiClient.delete(`/radar/scope-tier/documents/${documentId}/tags`, {
        data: input,
      })
      return response.data
    },
    onSuccess: () => {
      if (documentId) {
        void queryClient.invalidateQueries({ queryKey: [RADAR_SCOPE_TIER_KEY, 'document', documentId] })
      }
      void queryClient.invalidateQueries({ queryKey: [RADAR_SCOPE_TIER_KEY, 'documents'] })
      void queryClient.invalidateQueries({ queryKey: [RADAR_SCOPE_TIER_KEY, 'tags'] })
    },
  })
}
